import { IconStyles } from '@dynamic/ui';
import { InitOptions } from 'i18next';

import {
  CornerRadius,
  FormFactors,
  MenuBackgroundStyle,
  MenuFocusStyle,
} from '#/service/constants';
import { authActions } from '#/store/auth';

import { MenuType } from '../constants/menu';
import { MenuStyle } from './common.types';
import { MainConfig, MenuConfig, ThemeConfig } from './config.types';
import {
  defaultAppConfig,
  defaultConfig,
  defaultFeaturesConfig,
  defaultI18nextConfig,
  defaultThemeConfig,
} from './defaultConfig';
import {
  CoreFeatureConfig,
  FeaturesConfig,
  RawFeaturesConfig,
} from './features.types';
import { RawConfig, RawMenuConfig, RawThemeConfig } from './raw.types';

/** This function gets inline config in the document and transforms it for our domain. It also applies default values where necessary */
export function initializeConfig(
  config: RawConfig,
  i18nConfig?: InitOptions,
  staticFilePrefix?: string,
  version?: string,
  routerVersion?: string
): MainConfig {
  if (!config) {
    return defaultConfig;
  }

  // TODO#configStore: IMHO there should be a separate configStore that manages all app's configuration state.
  // When config is refactored then authConfig should be moved to the config store
  const authConfig = config.featureConfig.authentication;
  authActions.setConfig(authConfig);

  return {
    app: {
      staticFilePrefix,
      version,
      routerVersion,
      ...defaultAppConfig,
      ...config.app,
    },
    i18nextConfig: i18nConfig ?? defaultI18nextConfig,
    menusConfig: config.menusConfig ?? {
      groups: [],
      style: {},
    },
    theme: prepareTheme(config.theme, config.menusConfig?.style),
    menu: prepareMenu(config.menus),
    more: { settings: config.more },
    footer: config.footer ?? {
      enabled: false,
      copyright: '',
      contact: '',
      socials: [],
      columns: [],
    },
    features: prepareFeatures(config.features, config.featureConfig),
    featureConfig: config.featureConfig,
    pages: config.pages ?? {},
    terms: config.terms ?? {
      body: '',
      date: '',
    },
    restrictions: {
      geoblocked: false,
      ...config.restrictions, // from S3
      ...config.runtime?.restrictions, // from Core
    },
    runtime: config.runtime ?? {},
    integrations: {
      ...config.integrations,
      // the analytics integration configs are not yet coming from Core. they were added to S3 for now. to prevent the
      // web backend to overwrite S3 integrations with Core integrations, we renamed the S3 one to analyticsIntegrations,
      // and merging them here. when Core is ready, this can be removed. also remove from types, and from S3 config.
      ...config.analyticsIntegrations,
    },
    analyticsEvents: config.analyticsEvents ?? {},
    pluginIntegrations: config.pluginIntegrations ?? {},
  };
}

export function prepareTheme(
  theme: RawThemeConfig,
  menuStyle?: MenuStyle
): ThemeConfig {
  const newTheme: ThemeConfig = {
    color: { ...defaultConfig.theme.color, ...theme.color },
    images: theme.images,
    // We are hardcoding fonts configuration in the Web client
    fonts: defaultThemeConfig.fonts,
    gradientColor: theme.gradientColor,
    button: theme.button,
    menu: {
      focus: menuStyle?.focus ?? MenuFocusStyle.Box,
      background: menuStyle?.background ?? MenuBackgroundStyle.Gradient,
    },
    icon: theme.icon ?? {
      navigation: { style: IconStyles.Fill },
      parentalGuidance: { style: IconStyles.Fill },
    },
    cornerRadius: theme.cornerRadius ?? CornerRadius.SMALL,
    staticPathIcon: theme.staticPathIcon ?? '/static/plugins/icons/',
    logo: theme.logo,
  };

  return newTheme;
}

/**
 * It filters out not enabled features in a dynamic way and applies Features defined in the Core API
 **/
function prepareFeatures(
  features: RawFeaturesConfig,
  coreFeatures: CoreFeatureConfig
): FeaturesConfig {
  // TODO#configStore: IMHO there should be a separate configStore that manages all app's configuration state. And there this new config should be placed instead
  const result: Partial<FeaturesConfig> = {
    search: coreFeatures.search,
    playback: coreFeatures.playback,
    purchase: coreFeatures.purchase,
  };

  Object.entries(features ?? {})
    .filter(([_, value]) => Array.isArray(value))
    .forEach(([key, value]) => {
      result[key] = value.filter((f) => f.enabled);
    });

  // Let's use default features config as a fallback
  return { ...defaultFeaturesConfig, ...result };
}

export function prepareMenu(menus?: RawMenuConfig): MenuConfig {
  return {
    type: menus?.type ?? MenuType.TOP,
    mainMenuItems: menus?.[FormFactors.DESKTOP]?.menus[0]?.items ?? [],
    linkMenuItems: menus?.[FormFactors.DESKTOP]?.menus[1]?.items ?? [],
  };
}
